import type {
  GrowthCampaignAnalyticsResponse,
  GrowthCampaignComposerInput,
  GrowthCampaignCreateInput,
  GrowthCampaignDestinationKey,
  GrowthCampaignFrequencyPolicy,
  GrowthCampaignHistoryFilters,
  GrowthCampaignHistoryResponse,
  GrowthCampaignPreviewResponse,
  GrowthCampaignRuleFilters,
  GrowthCampaignRunDueResponse,
  GrowthCampaignSegmentDefinition,
  GrowthCampaignSegmentFieldDefinition,
  GrowthCampaignSegmentKey,
  GrowthCampaignSegmentMode,
  GrowthCampaignSendInput,
  GrowthCampaignSendResponse,
  GrowthCampaignTemplateKey,
  GrowthCampaignTemplateState,
  GrowthCampaignTemplateStatusResponse,
  GrowthCampaignUpdateInput,
  GrowthCampaignUpsertResponse,
} from "@/types/services/api-contracts";

import { callEdgeData } from "./_core/edge-call";
import { GROWTH_OPS_FUNCTION_NAME } from "./_core/function-names";

function normalizeGrowthCampaignPayload(
  input: Partial<GrowthCampaignComposerInput> & {
    scheduled_for?: string | null;
    dispatch_key?: string | null;
  },
): Record<string, unknown> {
  return {
    ...input,
    segment_key: input.segment_key ?? null,
    segment_mode: input.segment_mode ?? "preset",
    segment_filters: input.segment_filters ?? {},
    destination_key: input.destination_key ?? "my_bookings",
    action_label: input.action_label ?? null,
    feed_visible: input.feed_visible ?? true,
    push_enabled: input.push_enabled ?? false,
    ab_test_enabled: input.ab_test_enabled ?? false,
    scheduled_for: input.scheduled_for ?? null,
    frequency_policy: {
      cooldown_hours: input.frequency_policy?.cooldown_hours ?? 24,
      per_channel: input.frequency_policy?.per_channel ?? true,
    },
    variants: (input.variants ?? []).map((variant) => ({
      ...variant,
      action_label: variant.action_label ?? null,
      allocation_percent: variant.allocation_percent ?? 50,
    })),
    dispatch_key: input.dispatch_key ?? null,
  };
}

export const adminCampaignService = {
  async listSegments(): Promise<GrowthCampaignSegmentDefinition[]> {
    return callEdgeData<GrowthCampaignSegmentDefinition[]>(GROWTH_OPS_FUNCTION_NAME, "list-segments", {
      method: "GET",
    });
  },

  async listSegmentFields(): Promise<GrowthCampaignSegmentFieldDefinition[]> {
    return callEdgeData<GrowthCampaignSegmentFieldDefinition[]>(GROWTH_OPS_FUNCTION_NAME, "list-segment-fields", {
      method: "GET",
    });
  },

  async previewCampaign(input: {
    segment_key?: GrowthCampaignSegmentKey | null;
    segment_mode: GrowthCampaignSegmentMode;
    segment_filters?: GrowthCampaignRuleFilters;
    destination_key: GrowthCampaignDestinationKey;
    feed_visible?: boolean;
    push_enabled?: boolean;
    frequency_policy?: Partial<GrowthCampaignFrequencyPolicy>;
  }): Promise<GrowthCampaignPreviewResponse> {
    return callEdgeData<GrowthCampaignPreviewResponse>(GROWTH_OPS_FUNCTION_NAME, "preview-campaign", {
      body: normalizeGrowthCampaignPayload(input),
    });
  },

  async createCampaign(input: GrowthCampaignCreateInput): Promise<GrowthCampaignUpsertResponse> {
    return callEdgeData<GrowthCampaignUpsertResponse>(GROWTH_OPS_FUNCTION_NAME, "create-campaign", {
      body: {
        ...normalizeGrowthCampaignPayload(input),
        dispatch_mode: input.dispatch_mode,
        status: input.status ?? undefined,
        template_key: input.template_key ?? null,
      },
    });
  },

  async updateCampaign(input: GrowthCampaignUpdateInput): Promise<GrowthCampaignUpsertResponse> {
    return callEdgeData<GrowthCampaignUpsertResponse>(GROWTH_OPS_FUNCTION_NAME, "update-campaign", {
      body: {
        campaign_id: input.campaign_id,
        ...normalizeGrowthCampaignPayload(input),
        dispatch_mode: input.dispatch_mode,
        status: input.status ?? undefined,
        template_key: input.template_key ?? null,
      },
    });
  },

  async sendCampaignNow(input: GrowthCampaignSendInput): Promise<GrowthCampaignSendResponse> {
    return callEdgeData<GrowthCampaignSendResponse>(GROWTH_OPS_FUNCTION_NAME, "send-campaign-now", {
      body: {
        ...normalizeGrowthCampaignPayload(input),
        campaign_id: input.campaign_id ?? null,
        dispatch_key: input.dispatch_key ?? crypto.randomUUID(),
      },
    });
  },

  async cancelCampaign(campaignId: string): Promise<GrowthCampaignUpsertResponse> {
    return callEdgeData<GrowthCampaignUpsertResponse>(GROWTH_OPS_FUNCTION_NAME, "cancel-campaign", {
      body: { campaign_id: campaignId },
    });
  },

  async setTemplateStatus(input: {
    template_key: GrowthCampaignTemplateKey;
    status: GrowthCampaignTemplateState["status"];
  }): Promise<GrowthCampaignTemplateStatusResponse> {
    return callEdgeData<GrowthCampaignTemplateStatusResponse>(GROWTH_OPS_FUNCTION_NAME, "set-template-status", {
      body: input,
    });
  },

  async runDueCampaigns(input?: {
    dry_run?: boolean;
    target_campaign_id?: string | null;
    target_template_key?: GrowthCampaignTemplateKey | null;
    target_booking_id?: string | null;
    target_user_id?: string | null;
  }): Promise<GrowthCampaignRunDueResponse> {
    return callEdgeData<GrowthCampaignRunDueResponse>(GROWTH_OPS_FUNCTION_NAME, "run-due-campaigns", {
      body: {
        dry_run: input?.dry_run ?? false,
        target_campaign_id: input?.target_campaign_id ?? null,
        target_template_key: input?.target_template_key ?? null,
        target_booking_id: input?.target_booking_id ?? null,
        target_user_id: input?.target_user_id ?? null,
      },
    });
  },

  async listCampaignHistory(filters?: GrowthCampaignHistoryFilters): Promise<GrowthCampaignHistoryResponse> {
    return callEdgeData<GrowthCampaignHistoryResponse>(GROWTH_OPS_FUNCTION_NAME, "list-campaign-history", {
      body: {
        status: filters?.status ?? "all",
        segment_key: filters?.segment_key ?? "all",
        template_key: filters?.template_key ?? "all",
        period_days: filters?.period_days ?? 30,
      },
    });
  },

  async getCampaignAnalytics(campaignId?: string): Promise<GrowthCampaignAnalyticsResponse> {
    return callEdgeData<GrowthCampaignAnalyticsResponse>(GROWTH_OPS_FUNCTION_NAME, "get-campaign-analytics", {
      body: {
        campaign_id: campaignId ?? null,
      },
    });
  },
};
